import Phaser from 'phaser'

interface TouchControlConfig {
  joystickRadius: number
  joystickBaseColor: number
  joystickThumbColor: number
  joystickAlpha: number
  fireButtonRadius: number
  fireButtonColor: number
  fireButtonAlpha: number
}

export class TouchControlsManager {
  private scene: Phaser.Scene
  private config: TouchControlConfig

  // Joystick elements
  private joystickBase?: Phaser.GameObjects.Arc
  private joystickThumb?: Phaser.GameObjects.Arc
  private joystickZone?: Phaser.GameObjects.Zone
  private joystickPointer?: Phaser.Input.Pointer | null
  private joystickActive = false

  // Fire button elements
  private fireButton?: Phaser.GameObjects.Arc
  private fireButtonText?: Phaser.GameObjects.Text
  private fireButtonZone?: Phaser.GameObjects.Zone
  private fireButtonPressed = false

  // Input state
  private movementDirection = 0 // -1 left, 0 none, 1 right

  constructor(
    scene: Phaser.Scene,
    config?: Partial<TouchControlConfig>
  ) {
    this.scene = scene
    this.config = {
      joystickRadius: 60,
      joystickBaseColor: 0x00ff00,
      joystickThumbColor: 0x00ff00,
      joystickAlpha: 0.3,
      fireButtonRadius: 45,
      fireButtonColor: 0x00ff00,
      fireButtonAlpha: 0.3,
      ...config,
    }
  }

  create(): void {
    const { width, height } = this.scene.scale

    // Create virtual joystick (left side)
    this.createJoystick(width * 0.15, height - 100)

    // Create fire button (right side)
    this.createFireButton(width * 0.85, height - 100)
  }

  private createJoystick(x: number, y: number): void {
    // Base circle (fixed position)
    this.joystickBase = this.scene.add.circle(
      x,
      y,
      this.config.joystickRadius,
      this.config.joystickBaseColor,
      this.config.joystickAlpha
    )
    this.joystickBase.setStrokeStyle(2, this.config.joystickBaseColor, 0.6)
    this.joystickBase.setDepth(1000)
    this.joystickBase.setScrollFactor(0)

    // Thumb circle (moves within base)
    this.joystickThumb = this.scene.add.circle(
      x,
      y,
      this.config.joystickRadius * 0.5,
      this.config.joystickThumbColor,
      this.config.joystickAlpha + 0.2
    )
    this.joystickThumb.setStrokeStyle(2, this.config.joystickThumbColor, 0.8)
    this.joystickThumb.setDepth(1001)
    this.joystickThumb.setScrollFactor(0)

    // Interactive zone for joystick
    this.joystickZone = this.scene.add.zone(
      x,
      y,
      this.config.joystickRadius * 2.5,
      this.config.joystickRadius * 2.5
    )
    this.joystickZone.setInteractive()
    this.joystickZone.setScrollFactor(0)
    this.joystickZone.setDepth(999)

    // Touch events
    this.joystickZone.on('pointerdown', this.onJoystickDown, this)
    this.joystickZone.on('pointermove', this.onJoystickMove, this)
    this.joystickZone.on('pointerup', this.onJoystickUp, this)
    this.scene.input.on('pointerup', this.onJoystickGlobalUp, this)
  }

  private createFireButton(x: number, y: number): void {
    // Fire button circle
    this.fireButton = this.scene.add.circle(
      x,
      y,
      this.config.fireButtonRadius,
      this.config.fireButtonColor,
      this.config.fireButtonAlpha
    )
    this.fireButton.setStrokeStyle(3, this.config.fireButtonColor, 0.8)
    this.fireButton.setDepth(1000)
    this.fireButton.setScrollFactor(0)

    // Fire button text
    this.fireButtonText = this.scene.add.text(x, y, 'FIRE', {
      fontSize: '20px',
      color: '#00ff00',
      fontStyle: 'bold',
    })
    this.fireButtonText.setOrigin(0.5)
    this.fireButtonText.setDepth(1001)
    this.fireButtonText.setScrollFactor(0)
    this.fireButtonText.setAlpha(0.8)

    // Interactive zone for fire button
    this.fireButtonZone = this.scene.add.zone(
      x,
      y,
      this.config.fireButtonRadius * 2,
      this.config.fireButtonRadius * 2
    )
    this.fireButtonZone.setInteractive()
    this.fireButtonZone.setScrollFactor(0)
    this.fireButtonZone.setDepth(999)

    // Touch events
    this.fireButtonZone.on('pointerdown', this.onFireButtonDown, this)
    this.fireButtonZone.on('pointerup', this.onFireButtonUp, this)
    this.fireButtonZone.on('pointerout', this.onFireButtonUp, this)
  }

  private onJoystickDown(pointer: Phaser.Input.Pointer): void {
    this.joystickActive = true
    this.joystickPointer = pointer
    this.updateJoystickPosition(pointer)
  }

  private onJoystickMove(pointer: Phaser.Input.Pointer): void {
    if (this.joystickActive && this.joystickPointer?.id === pointer.id) {
      this.updateJoystickPosition(pointer)
    }
  }

  private onJoystickUp(pointer: Phaser.Input.Pointer): void {
    if (this.joystickPointer?.id === pointer.id) {
      this.resetJoystick()
    }
  }

  private onJoystickGlobalUp(pointer: Phaser.Input.Pointer): void {
    if (this.joystickActive && this.joystickPointer?.id === pointer.id) {
      this.resetJoystick()
    }
  }

  private updateJoystickPosition(pointer: Phaser.Input.Pointer): void {
    if (!this.joystickBase || !this.joystickThumb) return

    const baseX = this.joystickBase.x
    const baseY = this.joystickBase.y

    // Calculate distance and angle from base center
    const dx = pointer.x - baseX
    const dy = pointer.y - baseY
    const distance = Math.sqrt(dx * dx + dy * dy)
    const angle = Math.atan2(dy, dx)

    // Limit thumb movement to base radius
    const maxDistance = this.config.joystickRadius * 0.8
    const clampedDistance = Math.min(distance, maxDistance)

    // Update thumb position
    const thumbX = baseX + Math.cos(angle) * clampedDistance
    const thumbY = baseY + Math.sin(angle) * clampedDistance
    this.joystickThumb.setPosition(thumbX, thumbY)

    // Update movement direction (-1 left, 0 none, 1 right)
    // Only consider horizontal movement
    const threshold = maxDistance * 0.2
    if (Math.abs(dx) < threshold) {
      this.movementDirection = 0
    } else if (dx < 0) {
      this.movementDirection = -1
    } else {
      this.movementDirection = 1
    }
  }

  private resetJoystick(): void {
    if (!this.joystickBase || !this.joystickThumb) return

    this.joystickActive = false
    this.joystickPointer = null
    this.movementDirection = 0

    // Reset thumb to center
    this.joystickThumb.setPosition(this.joystickBase.x, this.joystickBase.y)
  }

  private onFireButtonDown(): void {
    if (!this.fireButton || !this.fireButtonText) return

    this.fireButtonPressed = true
    this.fireButton.setAlpha(this.config.fireButtonAlpha + 0.3)
    this.fireButtonText.setAlpha(1)
  }

  private onFireButtonUp(): void {
    if (!this.fireButton || !this.fireButtonText) return

    this.fireButtonPressed = false
    this.fireButton.setAlpha(this.config.fireButtonAlpha)
    this.fireButtonText.setAlpha(0.8)
  }

  getMovementDirection(): number {
    return this.movementDirection
  }

  isFireButtonPressed(): boolean {
    return this.fireButtonPressed
  }

  setVisible(visible: boolean): void {
    this.joystickBase?.setAlpha(visible ? this.config.joystickAlpha : 0)
    this.joystickThumb?.setAlpha(
      visible ? this.config.joystickAlpha + 0.2 : 0
    )
    this.fireButton?.setAlpha(visible ? this.config.fireButtonAlpha : 0)
    this.fireButtonText?.setAlpha(visible ? 0.8 : 0)
  }

  updatePosition(): void {
    // Update positions on screen resize
    const { width, height } = this.scene.scale

    if (this.joystickBase && this.joystickThumb) {
      const joystickX = width * 0.15
      const joystickY = height - 100

      this.joystickBase.setPosition(joystickX, joystickY)
      if (!this.joystickActive) {
        this.joystickThumb.setPosition(joystickX, joystickY)
      }
      this.joystickZone?.setPosition(joystickX, joystickY)
    }

    if (this.fireButton && this.fireButtonText && this.fireButtonZone) {
      const fireX = width * 0.85
      const fireY = height - 100

      this.fireButton.setPosition(fireX, fireY)
      this.fireButtonText.setPosition(fireX, fireY)
      this.fireButtonZone.setPosition(fireX, fireY)
    }
  }

  destroy(): void {
    this.joystickBase?.destroy()
    this.joystickThumb?.destroy()
    this.joystickZone?.destroy()
    this.fireButton?.destroy()
    this.fireButtonText?.destroy()
    this.fireButtonZone?.destroy()

    this.joystickBase = undefined
    this.joystickThumb = undefined
    this.joystickZone = undefined
    this.fireButton = undefined
    this.fireButtonText = undefined
    this.fireButtonZone = undefined
    this.joystickPointer = null
  }
}
